import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const stats = [
  { value: '5000+', label: '影视资源' },
  { value: '2000万+', label: '月活跃用户' },
  { value: '99.9%', label: '服务可用率' },
  { value: '24/7', label: '内容更新' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深影迷',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20man%20in%20casual%20style%2C%20warm%20lighting%2C%20clean%20background%2C%20friendly%20expression&width=120&height=120&seq=avatar-01&orientation=squarish',
    comment: '用了91电影视频两年多了，资源更新真的快。热门剧集基本上播出当天就能看，画质也很棒，真心推荐！',
  },
  {
    name: '李女士',
    role: '追剧达人',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20woman%20with%20warm%20smile%2C%20soft%20lighting%2C%20clean%20background&width=120&height=120&seq=avatar-02&orientation=squarish',
    comment: '界面简洁好用，不用注册就能看，对于我这种追剧狂魔来说简直是福音。分类也很清晰，找片超方便！',
  },
  {
    name: '王同学',
    role: '大学生',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Chinese%20student%20with%20glasses%2C%20friendly%20smile%2C%20clean%20background%2C%20warm%20lighting&width=120&height=120&seq=avatar-03&orientation=squarish',
    comment: '宿舍党必备！手机上看也不卡，流量用得也不多。国产剧、韩剧、美剧都有，资源超级全，大爱！',
  },
];

const timeline = [
  { year: '2020', title: '平台创立', desc: '91电影视频正式上线，致力于为用户提供免费、高质量的影视内容。' },
  { year: '2021', title: '内容扩充', desc: '影视资源库突破1000部，涵盖电影、电视剧、综艺三大品类。' },
  { year: '2022', title: '用户破百万', desc: '月活跃用户突破100万，成为华语地区最受欢迎的免费影视平台之一。' },
  { year: '2023', title: '技术升级', desc: '全面升级CDN加速网络，实现全国范围内的高清流畅播放体验。' },
  { year: '2024', title: '生态完善', desc: '推出个性化推荐系统，内容分类更加精细，用户体验全面优化。' },
  { year: '2026', title: '全新起航', desc: '资源库突破5000+，月活用户突破2000万，持续引领免费在线观影新潮流。' },
];

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'brand' | 'reputation' | 'contact'>('brand');

  const tabs = [
    { key: 'brand' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-customer-service-2-line' },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-12 md:py-20 text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-4">
              关于91电影视频
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              91电影视频是一个专注于提供高品质免费影视内容的在线平台。
              我们致力于让每一位用户都能享受到便捷、流畅、高清的观影体验，
              无需注册，无需付费，即点即播。
            </p>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-900/50">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tab Navigation */}
          <section className="w-full px-4 md:px-6 py-10">
            <div className="flex items-center justify-center gap-2 mb-10 bg-background-800 rounded-full p-1 max-w-sm mx-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-950'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="max-w-4xl mx-auto">
              {/* Brand */}
              {activeTab === 'brand' && (
                <div>
                  <div className="mb-14">
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6 text-center">
                      我们的故事
                    </h2>
                    <div className="bg-background-800 rounded-2xl p-8 border border-background-700/50">
                      <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                        91电影视频诞生于2020年，怀着"让每个人都能免费看好片"的初心，我们从一个小小的影视资源聚合网站起步，一步步成长为今天拥有超过2000万月活跃用户的综合性影视平台。
                      </p>
                      <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                        我们深知，在这个信息爆炸的时代，用户最需要的是简单、直接、高质量的观影体验。因此在产品设计上，我们始终坚持"即点即播、高清不缓冲"的核心理念，不断优化技术架构，让每一位用户都能享受到如丝般顺滑的观影感受。
                      </p>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        展望未来，91电影视频将继续秉持初心，不断丰富内容资源，提升服务质量，努力成为华语地区最值得信赖的免费影视平台。
                      </p>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                      发展历程
                    </h2>
                    <div className="space-y-0">
                      {timeline.map((item, idx) => (
                        <div key={item.year} className="flex gap-6">
                          <div className="flex flex-col items-center">
                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 border-2 border-primary-500/30 flex-shrink-0">
                              <span className="text-xs font-bold text-primary-400">{item.year.slice(2)}</span>
                            </div>
                            {idx < timeline.length - 1 && (
                              <div className="w-0.5 flex-1 bg-primary-500/20 my-1"></div>
                            )}
                          </div>
                          <div className={`pb-10 ${idx === timeline.length - 1 ? 'pb-0' : ''}`}>
                            <h4 className="text-sm font-heading font-semibold text-foreground-200 mb-1">
                              {item.title}
                            </h4>
                            <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Reputation */}
              {activeTab === 'reputation' && (
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                    用户口碑
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {testimonials.map((t, idx) => (
                      <div
                        key={idx}
                        className="bg-background-800 rounded-2xl p-6 border border-background-700/50 flex flex-col"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <img
                            src={t.avatar}
                            alt={t.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <h4 className="text-sm font-heading font-semibold text-foreground-200">
                              {t.name}
                            </h4>
                            <p className="text-xs text-foreground-500">{t.role}</p>
                          </div>
                        </div>
                        <p className="text-sm text-foreground-400 leading-relaxed flex-1">
                          &ldquo;{t.comment}&rdquo;
                        </p>
                        <div className="flex items-center gap-0.5 mt-4">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-primary-400 text-sm"></i>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact */}
              {activeTab === 'contact' && (
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                    联系我们
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                    <div className="bg-background-800 rounded-2xl p-6 border border-background-700/50">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary-500/10 rounded-xl mb-4">
                        <i className="ri-mail-line text-xl text-primary-400"></i>
                      </div>
                      <h4 className="text-base font-heading font-semibold text-foreground-200 mb-2">商务合作</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        如有商务合作意向，欢迎通过邮件与我们联系：
                        <br />
                        <a href="mailto:biz@91movie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                          biz@91movie.com
                        </a>
                      </p>
                    </div>

                    <div className="bg-background-800 rounded-2xl p-6 border border-background-700/50">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary-500/10 rounded-xl mb-4">
                        <i className="ri-customer-service-2-line text-xl text-primary-400"></i>
                      </div>
                      <h4 className="text-base font-heading font-semibold text-foreground-200 mb-2">用户支持</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        如在使用过程中遇到任何问题，请发送邮件至：
                        <br />
                        <a href="mailto:support@91movie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                          support@91movie.com
                        </a>
                      </p>
                    </div>

                    <div className="bg-background-800 rounded-2xl p-6 border border-background-700/50">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary-500/10 rounded-xl mb-4">
                        <i className="ri-error-warning-line text-xl text-primary-400"></i>
                      </div>
                      <h4 className="text-base font-heading font-semibold text-foreground-200 mb-2">版权申诉</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        如涉及版权相关问题，请联系：
                        <br />
                        <a href="mailto:copyright@91movie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                          copyright@91movie.com
                        </a>
                      </p>
                    </div>

                    <div className="bg-background-800 rounded-2xl p-6 border border-background-700/50">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary-500/10 rounded-xl mb-4">
                        <i className="ri-team-line text-xl text-primary-400"></i>
                      </div>
                      <h4 className="text-base font-heading font-semibold text-foreground-200 mb-2">加入我们</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        欢迎优秀人才加入91电影视频团队：
                        <br />
                        <a href="mailto:hr@91movie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                          hr@91movie.com
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div className="text-center">
                    <h4 className="text-base font-heading font-semibold text-foreground-200 mb-4">关注我们</h4>
                    <div className="flex items-center justify-center gap-4">
                      <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-background-800 hover:bg-primary-500/15 text-foreground-400 hover:text-primary-400 border border-background-700 hover:border-primary-500/30 transition-all cursor-pointer">
                        <i className="ri-weibo-line text-lg"></i>
                      </a>
                      <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-background-800 hover:bg-primary-500/15 text-foreground-400 hover:text-primary-400 border border-background-700 hover:border-primary-500/30 transition-all cursor-pointer">
                        <i className="ri-wechat-line text-lg"></i>
                      </a>
                      <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-background-800 hover:bg-primary-500/15 text-foreground-400 hover:text-primary-400 border border-background-700 hover:border-primary-500/30 transition-all cursor-pointer">
                        <i className="ri-tiktok-line text-lg"></i>
                      </a>
                      <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-background-800 hover:bg-primary-500/15 text-foreground-400 hover:text-primary-400 border border-background-700 hover:border-primary-500/30 transition-all cursor-pointer">
                        <i className="ri-bilibili-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于91电影视频',
            description: '91电影视频是一个专注于提供高品质免费影视内容的在线平台。致力于让每一位用户都能享受到便捷、流畅、高清的观影体验。',
            url: 'https://91movie.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '91电影视频',
              url: 'https://91movie.com',
            },
          }),
        }}
      />
    </>
  );
}